
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Terminal, Gamepad2, RefreshCw, Power, Loader2, MoreVertical, ShieldCheck, Tags, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import UnenrollDevice from "./UnenrollDevice";
import { useNotifications } from "@/contexts/NotificationContext";
import { RequestPrivilegeAccess } from "./RequestPrivilegeAccess";
import { useRemoteSession } from "@/hooks/useRemoteSession";
import { TagAssignmentDialog } from "@/components/tags/TagAssignmentDialog";
import { TagRemovalDialog } from "@/components/tags/TagRemovalDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const DeviceActions = () => {
  const { id: deviceId } = useParams();
  const { addNotification } = useNotifications();
  const { startSession } = useRemoteSession(deviceId || '');
  const [isTagAssignmentOpen, setIsTagAssignmentOpen] = useState(false);
  const [isTagRemovalOpen, setIsTagRemovalOpen] = useState(false);

  const { data: device } = useQuery({
    queryKey: ['device', deviceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('devices')
        .select('name')
        .eq('id', deviceId)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
  });

  const handleStartTerminal = async () => {
    try {
      await startSession.mutateAsync('terminal');
      toast.success('Terminal session started');
    } catch (error) {
      console.error('Failed to start terminal session:', error);
      toast.error('Failed to start terminal session');
    }
  };

  const handleStartRemoteControl = () => {
    window.open(`/device/${deviceId}/remote-control`, '_blank');
  };

  const triggerHeartbeat = async () => {
    if (!deviceId) {
      console.error('No device ID provided');
      return;
    }

    try {
      console.log('Triggering heartbeat for device:', deviceId);
      
      const { data: enrollmentToken, error } = await supabase.rpc(
        'get_device_enrollment_token', 
        { p_device_id: deviceId }
      );

      if (error || !enrollmentToken) {
        console.error('No enrollment token found for device:', deviceId, error);
        toast.error('Device enrollment token not found or access denied');
        return;
      }

      console.log('Invoking device-heartbeat function...');
      const { data, error: invokeError } = await supabase.functions.invoke('device-heartbeat', {
        body: {
          device_id: deviceId,
          enrollment_token: enrollmentToken
        }
      });

      if (invokeError) {
        console.error('Failed to trigger heartbeat:', invokeError);
        toast.error('Failed to trigger device heartbeat');
        return;
      }

      console.log('Heartbeat response:', data);
      toast.success('Device heartbeat triggered successfully');
    } catch (error) {
      console.error('Error in triggerHeartbeat:', error);
      toast.error('Failed to trigger device heartbeat');
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Actions">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Remote Access</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => startSession.mutateAsync('terminal')}>
            <Terminal className="mr-2 h-4 w-4" />
            Remote Terminal
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => window.open(`/device/${deviceId}/remote-control`, '_blank')}>
            <Gamepad2 className="mr-2 h-4 w-4" />
            Remote Control
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Management</DropdownMenuLabel>
          <DropdownMenuItem onSelect={(e) => {
            e.preventDefault();
            setIsTagAssignmentOpen(true);
          }}>
            <Tags className="mr-2 h-4 w-4" />
            Assign Tags
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={(e) => {
            e.preventDefault();
            setIsTagRemovalOpen(true);
          }}>
            <X className="mr-2 h-4 w-4" />
            Remove Tags
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          <DropdownMenuLabel>System</DropdownMenuLabel>
          {deviceId && (
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              <RequestPrivilegeAccess 
                deviceId={deviceId} 
                enrollmentToken={null}
              />
            </DropdownMenuItem>
          )}
          <DropdownMenuItem>
            <RefreshCw className="mr-2 h-4 w-4" />
            Update Agent
          </DropdownMenuItem>
          {deviceId && (
            <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-red-600 focus:text-red-600">
              <UnenrollDevice deviceId={deviceId} deviceName={deviceId} />
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Tag Management Dialogs */}
      {deviceId && device && (
        <>
          <TagAssignmentDialog
            open={isTagAssignmentOpen}
            onOpenChange={setIsTagAssignmentOpen}
            resourceId={deviceId}
            resourceType="device"
            resourceName={device.name}
            onAssignmentComplete={() => {
              setIsTagAssignmentOpen(false);
            }}
          />

          <TagRemovalDialog
            open={isTagRemovalOpen}
            onOpenChange={setIsTagRemovalOpen}
            deviceId={deviceId}
            deviceName={device.name}
            onRemovalComplete={() => {
              setIsTagRemovalOpen(false);
            }}
          />
        </>
      )}
    </>
  );
};

export default DeviceActions;
